/**
 * components/ChatInputBar.js — Phase 4 Week 2
 *
 * Input area at the bottom of the chatbot drawer.
 *  • Auto-growing textarea (1 → 4 rows)
 *  • Enter to send, Shift+Enter for newline
 *  • Send button disabled while streaming or input is empty
 *  • Character limit indicator at 500 chars
 *  • Dark mode via MUI palette tokens
 */
import React, { useRef, useEffect } from 'react';
import { Box, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import { Send } from '@mui/icons-material';

const MAX_CHARS = 500;

export default function ChatInputBar({ onSend, disabled = false, stompStatus = 'connected' }) {
  const [value, setValue] = React.useState('');
  const textRef = useRef(null);

  // Focus textarea when it becomes enabled (e.g. streaming ends)
  useEffect(() => {
    if (!disabled) textRef.current?.focus();
  }, [disabled]);

  const handleSend = () => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setValue('');
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const remaining = MAX_CHARS - value.length;
  const nearLimit = remaining <= 50;

  return (
    <Box
      sx={{
        px: 1.5, py: 1.25,
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
        <TextField
          inputRef={textRef}
          multiline
          maxRows={4}
          fullWidth
          placeholder="Ask anything about your HR data…"
          value={value}
          onChange={(e) => {
            if (e.target.value.length <= MAX_CHARS) setValue(e.target.value);
          }}
          onKeyDown={handleKey}
          disabled={disabled}
          size="small"
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              fontSize: '1rem',
              bgcolor: (t) =>
                t.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.04)'
                  : 'rgba(0,0,0,0.02)',
              '& fieldset': { borderColor: 'divider' },
              '&:hover fieldset': { borderColor: 'primary.light' },
              '&.Mui-focused fieldset': { borderColor: 'primary.main' },
            },
          }}
        />

        <Tooltip
          title={
            stompStatus !== 'connected'
              ? 'Connecting…'
              : disabled
                ? 'Waiting for response…'
                : 'Send (Enter)'
          }
          placement="top"
        >
          <span>
            <IconButton
              onClick={handleSend}
              disabled={disabled || !value.trim()}
              color="primary"
              sx={{
                width: 40, height: 40,
                bgcolor: (t) =>
                  disabled || !value.trim()
                    ? 'transparent'
                    : 'primary.main',
                color: (t) =>
                  disabled || !value.trim()
                    ? 'action.disabled'
                    : '#fff',
                '&:hover': {
                  bgcolor: (t) =>
                    disabled || !value.trim()
                      ? 'transparent'
                      : 'primary.dark',
                },
                transition: 'all 180ms ease',
                flexShrink: 0,
              }}
            >
              <Send sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Character counter — only shown near limit */}
      {nearLimit && (
        <Typography
          variant="caption"
          sx={{
            display: 'block', textAlign: 'right', mt: 0.5,
            color: remaining <= 10 ? 'error.main' : 'text.disabled',
            fontSize: '0.7rem',
          }}
        >
          {remaining} characters remaining
        </Typography>
      )}
    </Box>
  );
}
